import { useCardPrints, type CardPrint } from '@/hooks/useCollection';
import { treatmentLabel, sortPrints } from '@/lib/treatment';
import { formatPrice } from '@/lib/card-prices';

interface Props {
  cardId: string;
  value: string | null;        // selected print id, or null = no specific print
  onChange: (printId: string | null, print: CardPrint | null) => void;
  className?: string;
}

/** Inline chip selector for choosing which printing/treatment of a card to add. */
export function TreatmentPicker({ cardId, value, onChange, className }: Props) {
  const { data: prints, isLoading } = useCardPrints(cardId);
  const sorted = prints ? sortPrints(prints) : [];

  if (isLoading) {
    return <p className={`text-[10px] text-muted-foreground font-body ${className || ''}`}>Loading variants…</p>;
  }
  if (!sorted.length) {
    return <p className={`text-[10px] text-muted-foreground font-body ${className || ''}`}>No print variants available.</p>;
  }

  return (
    <div className={className}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-body mb-1.5">
        Treatment
      </p>
      <div className="flex flex-wrap gap-1.5">
        {sorted.map((p) => {
          const selected = value === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onChange(p.id, p)}
              className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium font-body transition-colors ${
                selected
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/70'
              }`}
            >
              <span>{treatmentLabel(p.treatment)}</span>
              {p.price_usd != null && (
                <span className={`text-[10px] ${selected ? 'opacity-90' : 'text-primary'}`}>
                  {formatPrice(Number(p.price_usd))}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
